import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AlignBottomIcon, AlignCenterHorizontallyIcon, AlignCenterVerticallyIcon, AlignLeftIcon, AlignRightIcon, AlignTopIcon, ColorWheelIcon, MixerHorizontalIcon, } from '@radix-ui/react-icons';
import { Select } from '@radix-ui/themes';
import { useUIStore, useMovieStore, useSelectionStore, useTimelineStore, useEditorStore } from '../../stores';
import { useSignalR } from '../../hooks';
import { Toolpad } from './Toolpad';
import { SidebarColumn } from './SidebarColumn';
import { ALL_STROKE_SIDES, DEFAULT_FILL_GRADIENT, DEFAULT_OBJECT_GLOW, } from '../../types';
import { generateId } from '../../util/utils';
import { resolveStateTarget } from '../../util/resolveStateTarget';
import { seedNewKeyframeState } from '../../util/newKeyframeSeed';
import { PropertyInput } from '../ui/PropertyInput';
import { serializeShapeBody, shapeBodyFromSceneObject } from '../../util/shapeBody';
import { fontSizeForTextResize, isBorderNone, isFillNone, serializeTextBody, textBodyFromSceneObject, } from '../../util/textBody';
import { serializeImageBodyForPersist } from '../../util/imageBody';
import { mergeFillGradient, mergeObjectGlow, mergeObjectShadow, resolveFillMode, } from '../../util/objectVisualStyle';
import { hasAnyStrokeSide, resolveStrokeSides, supportsStrokeSides, toggleStrokeSide, } from '../../util/strokeSides';
import { useTranslation } from '../../i18n';
import { keyframeStatePatchJson, sceneObjectTransformPatchJson } from '../../util/sceneObjectLayoutProperties';
const RIGHT_TOOLPAD_ORDER = {
    stylePad: 0,
    objectProperties: 1,
    alignPad: 2,
};
const RIGHT_RAIL_META = {
    stylePad: {
        icon: _jsx(ColorWheelIcon, { width: 16, height: 16 }),
        titleKey: 'rightSidebar.style.title',
    },
    objectProperties: {
        icon: _jsx(MixerHorizontalIcon, { width: 16, height: 16 }),
        titleKey: 'rightSidebar.properties.title',
    },
    alignPad: {
        icon: _jsx(AlignCenterHorizontallyIcon, { width: 16, height: 16 }),
        titleKey: 'rightSidebar.align.title',
    },
};
export function RightSidebar() {
    const { toolpads, rightSidebarOpen, setRightSidebar, openToolpad, } = useUIStore();
    const rightToolpads = Object.values(toolpads)
        .filter((tp) => tp.position === 'right')
        .sort((a, b) => {
        const aRank = RIGHT_TOOLPAD_ORDER[a.id] ?? 100;
        const bRank = RIGHT_TOOLPAD_ORDER[b.id] ?? 100;
        return aRank - bRank;
    });
    const railItems = rightToolpads.flatMap((tp) => {
        const meta = RIGHT_RAIL_META[tp.id];
        if (!meta)
            return [];
        return [{ id: tp.id, icon: meta.icon, titleKey: meta.titleKey }];
    });
    const handleExpandToolpad = (id) => {
        openToolpad(id);
        setRightSidebar(true);
    };
    return (_jsx(SidebarColumn, { side: "right", isOpen: rightSidebarOpen, onCollapse: () => setRightSidebar(false), onExpandToolpad: handleExpandToolpad, items: railItems, children: rightToolpads.map((tp) => (_jsx(ToolpadWrapper, { toolpadId: tp.id }, tp.id))) }));
}
function ToolpadWrapper({ toolpadId }) {
    switch (toolpadId) {
        case 'stylePad':
            return _jsx(StyleToolpad, {});
        case 'objectProperties':
            return _jsx(ObjectPropertiesToolpad, {});
        case 'alignPad':
            return _jsx(AlignToolpad, {});
        default:
            return null;
    }
}
const EASING_OPTIONS = [
    'linear',
    'easeIn',
    'easeOut',
    'easeInOut',
    'bounce',
    'elastic',
    'back',
];
const EASING_LABEL_KEYS = {
    linear: 'rightSidebar.properties.linear',
    easeIn: 'rightSidebar.properties.easeIn',
    easeOut: 'rightSidebar.properties.easeOut',
    easeInOut: 'rightSidebar.properties.easeInOut',
    bounce: 'rightSidebar.properties.bounce',
    elastic: 'rightSidebar.properties.elastic',
    back: 'rightSidebar.properties.back',
};
function StyleToolpad() {
    const { t } = useTranslation();
    const { toolpads, toggleToolpad } = useUIStore();
    const { currentScene, updateSceneObject } = useMovieStore();
    const { selectedIds, focusedId } = useSelectionStore();
    const { fillColor: defaultFillColor, strokeColor: defaultStrokeColor, strokeWidth: defaultStrokeWidth, strokeStyle: defaultStrokeStyle } = useEditorStore();
    const { sceneObjectUpdateContent, connect, status: signalRStatus } = useSignalR(false);
    const persistTimerRef = useRef(null);
    const lastFillColorRef = useRef(defaultFillColor);
    const isOpen = toolpads.stylePad.isOpen;
    const activeSelectedId = focusedId && selectedIds.includes(focusedId) ? focusedId : selectedIds[0];
    const selectedObject = activeSelectedId
        ? currentScene?.objects.find((o) => o.id === activeSelectedId)
        : undefined;
    const isShape = selectedObject?.type === 'shape';
    const isText = selectedObject?.type === 'text';
    const isImage = selectedObject?.type === 'image';
    const supportsFill = isShape || isText;
    const supportsEffects = isShape || isText || isImage;
    const supportsStylePanel = supportsFill || supportsEffects;
    const fill = selectedObject?.fill ?? defaultFillColor;
    const stroke = selectedObject?.stroke ?? defaultStrokeColor;
    const strokeWidth = selectedObject?.strokeWidth ?? defaultStrokeWidth;
    const strokeStyle = selectedObject?.strokeStyle ?? defaultStrokeStyle;
    const fontSize = selectedObject?.fontSize ?? 16;
    const textAlign = selectedObject?.textAlign ?? 'center';
    const strokeSides = resolveStrokeSides(selectedObject?.strokeSides);
    const fillMode = selectedObject ? resolveFillMode(selectedObject) : 'solid';
    const fillGradient = mergeFillGradient(selectedObject?.fill, selectedObject?.fillGradient);
    const shadow = mergeObjectShadow(selectedObject?.shadow);
    const glow = mergeObjectGlow(selectedObject?.glow);
    const isBoxShape = isShape &&
        supportsStrokeSides(selectedObject?.shapeType) &&
        selectedObject?.shapeType !== 'path' &&
        selectedObject?.shapeType !== 'triangle';
    const fillIsNone = selectedObject ? isFillNone(selectedObject) : true;
    const borderIsNone = selectedObject ? isBorderNone(selectedObject) : true;
    const [styleHint, setStyleHint] = useState(null);
    useEffect(() => {
        if (!fillIsNone && fill !== 'none') {
            lastFillColorRef.current = fill;
        }
    }, [fill, fillIsNone]);
    useEffect(() => {
        setStyleHint(null);
    }, [selectedObject?.id]);
    useEffect(() => {
        return () => {
            if (persistTimerRef.current)
                clearTimeout(persistTimerRef.current);
        };
    }, []);
    const persistObjectStyle = useCallback(async (object) => {
        const sceneObjectId = Number.parseInt(object.id, 10);
        if (!Number.isFinite(sceneObjectId))
            return;
        const body = object.type === 'shape'
            ? serializeShapeBody(shapeBodyFromSceneObject(object))
            : object.type === 'text'
                ? serializeTextBody(textBodyFromSceneObject(object))
                : object.type === 'image'
                    ? serializeImageBodyForPersist(object)
                    : null;
        if (body == null)
            return;
        try {
            if (signalRStatus !== 'Connected')
                await connect();
            await sceneObjectUpdateContent({
                id: sceneObjectId,
                type: object.type,
                body,
            });
        }
        catch (error) {
            console.error('[StyleToolpad] Failed to persist object style:', error);
        }
    }, [connect, sceneObjectUpdateContent, signalRStatus]);
    const schedulePersistStyle = useCallback((nextObject) => {
        if (persistTimerRef.current)
            clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(() => {
            void persistObjectStyle(nextObject);
        }, 300);
    }, [persistObjectStyle]);
    const applyStylePatch = useCallback((patch) => {
        if (!selectedObject ||
            (selectedObject.type !== 'shape' &&
                selectedObject.type !== 'text' &&
                selectedObject.type !== 'image')) {
            return;
        }
        const nextObject = { ...selectedObject, ...patch };
        updateSceneObject(selectedObject.id, patch);
        schedulePersistStyle(nextObject);
    }, [selectedObject, updateSceneObject, schedulePersistStyle]);
    const setFillColor = (value) => {
        setStyleHint(null);
        applyStylePatch({
            fill: value,
            fillMode: fillMode === 'none' ? 'solid' : fillMode,
            fillGradient: fillMode === 'gradient'
                ? { ...fillGradient, colorStart: value }
                : selectedObject?.fillGradient,
        });
    };
    const setFillModeValue = (mode) => {
        if (mode === 'none') {
            if (isShape && borderIsNone) {
                setStyleHint(t('rightSidebar.style.fillBorderConflict'));
                return;
            }
            setStyleHint(null);
            applyStylePatch({ fillMode: 'none', fill: 'none' });
            return;
        }
        setStyleHint(null);
        if (mode === 'gradient') {
            const start = lastFillColorRef.current || defaultFillColor;
            applyStylePatch({
                fillMode: 'gradient',
                fill: start,
                fillGradient: {
                    ...DEFAULT_FILL_GRADIENT,
                    ...selectedObject?.fillGradient,
                    colorStart: selectedObject?.fillGradient?.colorStart ?? start,
                    colorEnd: selectedObject?.fillGradient?.colorEnd ?? DEFAULT_FILL_GRADIENT.colorEnd,
                },
            });
            return;
        }
        applyStylePatch({
            fillMode: 'solid',
            fill: lastFillColorRef.current || defaultFillColor,
        });
    };
    const setGradientColorEnd = (value) => {
        setStyleHint(null);
        applyStylePatch({
            fillMode: 'gradient',
            fillGradient: { ...fillGradient, colorEnd: value },
        });
    };
    const setGradientAngle = (angle) => {
        setStyleHint(null);
        applyStylePatch({
            fillMode: 'gradient',
            fillGradient: { ...fillGradient, angle },
        });
    };
    const setStrokeColor = (value) => {
        setStyleHint(null);
        applyStylePatch({ stroke: value, strokeStyle: strokeStyle === 'none' ? 'solid' : strokeStyle });
    };
    const setStrokeWidth = (value) => {
        setStyleHint(null);
        const width = Math.max(0, value);
        applyStylePatch({
            strokeWidth: width,
            strokeStyle: width <= 0 ? 'none' : strokeStyle === 'none' ? 'solid' : strokeStyle,
        });
    };
    const setStrokeStyle = (value) => {
        if (value === 'none') {
            if (fillIsNone) {
                setStyleHint(t('rightSidebar.style.fillBorderConflict'));
                return;
            }
            setStyleHint(null);
            applyStylePatch({ strokeStyle: 'none', strokeWidth: 0 });
            return;
        }
        setStyleHint(null);
        const nextSides = hasAnyStrokeSide(selectedObject?.strokeSides)
            ? resolveStrokeSides(selectedObject?.strokeSides)
            : { ...ALL_STROKE_SIDES };
        applyStylePatch({
            strokeStyle: value,
            strokeWidth: strokeWidth > 0 ? strokeWidth : defaultStrokeWidth,
            strokeSides: nextSides,
        });
    };
    const setStrokeSide = (side, enabled) => {
        if (!selectedObject)
            return;
        if (borderIsNone && enabled) {
            setStyleHint(null);
            applyStylePatch({
                strokeStyle: 'solid',
                strokeWidth: strokeWidth > 0 ? strokeWidth : defaultStrokeWidth,
                strokeSides: {
                    top: side === 'top',
                    right: side === 'right',
                    bottom: side === 'bottom',
                    left: side === 'left',
                },
            });
            return;
        }
        const nextSides = toggleStrokeSide(selectedObject.strokeSides, side, enabled);
        if (!hasAnyStrokeSide(nextSides)) {
            if (fillIsNone) {
                setStyleHint(t('rightSidebar.style.fillBorderConflict'));
                return;
            }
            setStyleHint(null);
            applyStylePatch({ strokeSides: nextSides, strokeStyle: 'none', strokeWidth: 0 });
            return;
        }
        setStyleHint(null);
        applyStylePatch({
            strokeSides: nextSides,
            strokeStyle: strokeStyle === 'none' ? 'solid' : strokeStyle,
            strokeWidth: strokeWidth > 0 ? strokeWidth : defaultStrokeWidth,
        });
    };
    const setFontSize = (value) => {
        applyStylePatch({ fontSize: Math.max(3, Math.min(200, value)) });
    };
    const setTextAlign = (value) => {
        applyStylePatch({ textAlign: value });
    };
    const setShadowEnabled = (enabled) => {
        applyStylePatch({
            shadow: { ...shadow, enabled },
        });
    };
    const patchShadow = (patch) => {
        applyStylePatch({
            shadow: { ...shadow, enabled: true, ...patch },
        });
    };
    const setGlowEnabled = (enabled) => {
        applyStylePatch({
            glow: { ...glow, enabled },
        });
    };
    const patchGlow = (patch) => {
        applyStylePatch({
            glow: { ...glow, enabled: true, ...patch },
        });
    };
    const fillColorValue = fillMode === 'none' ? lastFillColorRef.current : fill === 'none' ? lastFillColorRef.current : fill;
    return (_jsxs(Toolpad, { title: t('rightSidebar.style.title'), isOpen: isOpen, onToggle: () => toggleToolpad('stylePad'), children: [!selectedObject && (_jsx("div", { className: "property-section", children: _jsx("div", { className: "no-selection-message", children: t('rightSidebar.style.selectObject') }) })), selectedObject && !supportsStylePanel && (_jsx("div", { className: "property-section", children: _jsx("div", { className: "no-selection-message", children: t('rightSidebar.style.shapeOrTextOnly') }) })), isText && (_jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.style.text') }), _jsxs("div", { className: "keyframe-info", style: { gap: 8 }, children: [_jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.fontSize') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: 3, max: 200, value: fontSize, onChange: (e) => setFontSize(Number.parseInt(e.target.value, 10)), "aria-label": t('rightSidebar.style.fontSizeAria') }), _jsx(PropertyInput, { className: "property-input-inline", value: fontSize, min: 3, max: 200, onChange: setFontSize, "aria-label": t('rightSidebar.style.fontSizePxAria') }), _jsx("span", { className: "unit", children: "px" })] })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.align') }), _jsxs(Select.Root, { value: textAlign, onValueChange: (value) => setTextAlign(value), size: "1", children: [_jsx(Select.Trigger, { variant: "surface", className: "style-select-trigger", "aria-label": t('rightSidebar.style.textAlignAria') }), _jsxs(Select.Content, { position: "popper", sideOffset: 4, children: [_jsx(Select.Item, { value: "left", children: t('rightSidebar.style.left') }), _jsx(Select.Item, { value: "center", children: t('rightSidebar.style.center') }), _jsx(Select.Item, { value: "right", children: t('rightSidebar.style.right') }), _jsx(Select.Item, { value: "justify", children: t('rightSidebar.style.justify') })] })] })] })] })] })), supportsFill && (_jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.style.colors') }), _jsxs("div", { className: "keyframe-info", style: { gap: 8 }, children: [_jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.fill') }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsxs(Select.Root, { value: fillMode, onValueChange: (value) => setFillModeValue(value), size: "1", children: [_jsx(Select.Trigger, { variant: "surface", className: "style-select-trigger style-select-trigger--compact", "aria-label": t('rightSidebar.style.fillModeAria') }), _jsxs(Select.Content, { position: "popper", sideOffset: 4, children: [_jsx(Select.Item, { value: "solid", children: t('rightSidebar.style.fillSolid') }), _jsx(Select.Item, { value: "gradient", children: t('rightSidebar.style.fillGradient') }), _jsx(Select.Item, { value: "none", children: t('common.none') })] })] }), fillMode !== 'gradient' && (_jsx("input", { type: "color", value: fillColorValue, onChange: (e) => setFillColor(e.target.value), className: "color-style-input", title: t('rightSidebar.style.fillColor'), disabled: fillMode === 'none' }))] })] }), fillMode === 'gradient' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.gradientStart') }), _jsx("input", { type: "color", value: fillGradient.colorStart, onChange: (e) => setFillColor(e.target.value), className: "color-style-input", title: t('rightSidebar.style.gradientStart') })] }), _jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.gradientEnd') }), _jsx("input", { type: "color", value: fillGradient.colorEnd, onChange: (e) => setGradientColorEnd(e.target.value), className: "color-style-input", title: t('rightSidebar.style.gradientEnd') })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.gradientAngle') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: 0, max: 360, value: fillGradient.angle, onChange: (e) => setGradientAngle(Number.parseInt(e.target.value, 10)), "aria-label": t('rightSidebar.style.gradientAngleAria') }), _jsx(PropertyInput, { className: "property-input-inline", value: fillGradient.angle, min: 0, max: 360, onChange: setGradientAngle, "aria-label": t('rightSidebar.style.gradientAngleAria') }), _jsx("span", { className: "unit", children: "\u00B0" })] })] })] })), isShape && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.border') }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsxs(Select.Root, { value: borderIsNone ? 'none' : strokeStyle === 'none' ? 'solid' : strokeStyle, onValueChange: (value) => {
                                                            if (value === 'none') {
                                                                setStrokeStyle('none');
                                                            }
                                                            else {
                                                                setStrokeStyle(value);
                                                            }
                                                        }, size: "1", children: [_jsx(Select.Trigger, { variant: "surface", className: "style-select-trigger style-select-trigger--compact", "aria-label": t('rightSidebar.style.borderTypeAria') }), _jsxs(Select.Content, { position: "popper", sideOffset: 4, children: [_jsx(Select.Item, { value: "solid", children: t('rightSidebar.style.solid') }), _jsx(Select.Item, { value: "dashed", children: t('rightSidebar.style.dashed') }), _jsx(Select.Item, { value: "dotted", children: t('rightSidebar.style.dotted') }), _jsx(Select.Item, { value: "none", children: t('common.none') })] })] }), _jsx("input", { type: "color", value: stroke, onChange: (e) => setStrokeColor(e.target.value), className: "color-style-input", title: t('rightSidebar.style.borderColor'), disabled: borderIsNone })] })] }), _jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.size') }), _jsx(PropertyInput, { className: "property-input-inline", value: strokeWidth, min: 0, max: 40, onChange: setStrokeWidth, disabled: borderIsNone, "aria-label": t('rightSidebar.style.borderSizeAria') })] }), supportsStrokeSides(selectedObject?.shapeType) && (_jsxs("div", { className: "color-style-row style-border-sides-row", children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.sides') }), _jsx("div", { className: "style-border-sides", role: "group", "aria-label": t('rightSidebar.style.sidesAria'), children: [
                                                    ['top', 'rightSidebar.style.sideTop'],
                                                    ['right', 'rightSidebar.style.sideRight'],
                                                    ['bottom', 'rightSidebar.style.sideBottom'],
                                                    ['left', 'rightSidebar.style.sideLeft'],
                                                ].map(([side, labelKey]) => {
                                                    const active = !borderIsNone && strokeSides[side];
                                                    return (_jsx("button", { type: "button", className: `style-border-side-link ${active ? 'active' : ''}`, "aria-pressed": active, onClick: () => setStrokeSide(side, !active), children: t(labelKey) }, side));
                                                }) })] }))] })), (styleHint || (isShape && fillIsNone && borderIsNone)) && (_jsx("div", { className: "no-selection-message", style: { marginTop: 4, color: 'var(--color-warning, #f59e0b)' }, children: styleHint ?? t('rightSidebar.style.fillBorderConflict') }))] })] })), supportsEffects && (_jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.style.effects') }), _jsxs("div", { className: "keyframe-info", style: { gap: 8 }, children: [_jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.shadow') }), _jsx("button", { type: "button", className: `style-border-side-link ${shadow.enabled ? 'active' : ''}`, "aria-pressed": shadow.enabled, onClick: () => setShadowEnabled(!shadow.enabled), children: shadow.enabled ? t('rightSidebar.style.enabled') : t('rightSidebar.style.disabled') })] }), shadow.enabled && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.shadowColor') }), _jsx("input", { type: "color", value: shadow.color.startsWith('#') ? shadow.color.slice(0, 7) : '#000000', onChange: (e) => patchShadow({ color: e.target.value }), className: "color-style-input", title: t('rightSidebar.style.shadowColor') })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.shadowOffsetX') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: -40, max: 40, value: shadow.offsetX, onChange: (e) => patchShadow({ offsetX: Number.parseInt(e.target.value, 10) }) }), _jsx(PropertyInput, { className: "property-input-inline", value: shadow.offsetX, min: -40, max: 40, onChange: (v) => patchShadow({ offsetX: v }) })] })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.shadowOffsetY') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: -40, max: 40, value: shadow.offsetY, onChange: (e) => patchShadow({ offsetY: Number.parseInt(e.target.value, 10) }) }), _jsx(PropertyInput, { className: "property-input-inline", value: shadow.offsetY, min: -40, max: 40, onChange: (v) => patchShadow({ offsetY: v }) })] })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.shadowBlur') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: 0, max: 60, value: shadow.blur, onChange: (e) => patchShadow({ blur: Number.parseInt(e.target.value, 10) }) }), _jsx(PropertyInput, { className: "property-input-inline", value: shadow.blur, min: 0, max: 60, onChange: (v) => patchShadow({ blur: v }) })] })] }), isBoxShape && (_jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.shadowSpread') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: -20, max: 40, value: shadow.spread, onChange: (e) => patchShadow({ spread: Number.parseInt(e.target.value, 10) }) }), _jsx(PropertyInput, { className: "property-input-inline", value: shadow.spread, min: -20, max: 40, onChange: (v) => patchShadow({ spread: v }) })] })] }))] })), _jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.glow') }), _jsx("button", { type: "button", className: `style-border-side-link ${glow.enabled ? 'active' : ''}`, "aria-pressed": glow.enabled, onClick: () => setGlowEnabled(!glow.enabled), children: glow.enabled ? t('rightSidebar.style.enabled') : t('rightSidebar.style.disabled') })] }), glow.enabled && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "color-style-row", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }, children: [_jsx("span", { className: "color-style-label", children: t('rightSidebar.style.glowColor') }), _jsx("input", { type: "color", value: glow.color.startsWith('#') ? glow.color.slice(0, 7) : DEFAULT_OBJECT_GLOW.color, onChange: (e) => patchGlow({ color: e.target.value }), className: "color-style-input", title: t('rightSidebar.style.glowColor') })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.style.glowBlur') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: 0, max: 80, value: glow.blur, onChange: (e) => patchGlow({ blur: Number.parseInt(e.target.value, 10) }) }), _jsx(PropertyInput, { className: "property-input-inline", value: glow.blur, min: 0, max: 80, onChange: (v) => patchGlow({ blur: v }) })] })] })] }))] })] }))] }));
}
function ObjectPropertiesToolpad() {
    const { t } = useTranslation();
    const { toolpads, toggleToolpad, keepAspectRatio, toggleKeepAspectRatio, stageResizeEnabled, stageRotateEnabled, toggleStageResizeEnabled, toggleStageRotateEnabled, } = useUIStore();
    const { currentScene, currentLayer, updateSceneObject, currentMovieResponse } = useMovieStore();
    const { selectedIds, focusedId, setFocused } = useSelectionStore();
    const { layers, keyframes, tweens, selectedKeyframe, selectedTween, updateKeyframe, addKeyframe, updateTween, } = useTimelineStore();
    const { currentFrame } = useEditorStore();
    const { tweenSave, sceneObjectPatchStateProperties, sceneObjectUpdateContent, sceneObjectSetVisibility, sceneObjectSetLock, connect, status: signalRStatus, } = useSignalR();
    const isOpen = toolpads.objectProperties.isOpen;
    const [activeTab, setActiveTab] = useState('object');
    const [stateObjectId, setStateObjectId] = useState(null);
    const [copyingDirection, setCopyingDirection] = useState(null);
    const layoutPersistTimerRef = useRef(null);
    const textPersistTimerRef = useRef(null);
    // Tween seçilince tween tab; timeline seçimi kalkınca (ör. yeni çizim) object tab.
    useEffect(() => {
        if (selectedTween) {
            setActiveTab('tween');
            return;
        }
        setActiveTab('object');
    }, [selectedTween?.layerId, selectedTween?.startFrame]);
    useEffect(() => {
        return () => {
            if (layoutPersistTimerRef.current)
                clearTimeout(layoutPersistTimerRef.current);
            if (textPersistTimerRef.current)
                clearTimeout(textPersistTimerRef.current);
        };
    }, []);
    const selectedLayerId = selectedTween?.layerId ?? selectedKeyframe?.layerId ?? currentLayer?.id ?? null;
    const selectedLayerIdNum = selectedLayerId ? Number(selectedLayerId) : NaN;
    const selectedObjectsFromScene = currentScene?.objects.filter((o) => selectedIds.includes(o.id)) ?? [];
    const activeSelectedId = focusedId && selectedIds.includes(focusedId) ? focusedId : selectedIds[0] ?? null;
    const selectedObjectFromScene = activeSelectedId
        ? selectedObjectsFromScene.find((o) => o.id === activeSelectedId) ??
            selectedObjectsFromScene[0]
        : undefined;
    const currentLayerIdNum = currentLayer ? Number(currentLayer.id) : NaN;
    const layerForSelection = selectedLayerId ? layers.find((l) => l.id === selectedLayerId) : null;
    const selectedObjectFromLayer = selectedLayerId
        ? currentScene?.objects.find((o) => (layerForSelection?.objectId && o.id === layerForSelection.objectId) ||
            (!Number.isNaN(selectedLayerIdNum) && o.layerId === selectedLayerIdNum))
        : currentLayer
            ? currentScene?.objects.find((o) => o.id === currentLayer.objectId ||
                o.id === currentLayer.id ||
                (!Number.isNaN(currentLayerIdNum) && o.layerId === currentLayerIdNum))
            : undefined;
    const selectedObject = selectedObjectFromScene ?? selectedObjectFromLayer;
    const objectKeyframes = selectedLayerId ? keyframes[selectedLayerId] ?? [] : [];
    const objectTweens = selectedLayerId ? tweens[selectedLayerId] ?? [] : [];
    const showSelectionSwitcher = selectedObjectsFromScene.length > 1;
    const hasObjectSelection = !!selectedObjectFromScene;
    const hasTweenSelection = !!(selectedTween && selectedLayerId);
    const hasKeyframeSelection = !!(selectedKeyframe && selectedLayerId);
    const hasTimelineSelection = hasTweenSelection || hasKeyframeSelection;
    const showProperties = hasObjectSelection || hasTimelineSelection;
    const stateTarget = selectedLayerId != null
        ? resolveStateTarget({
            layerId: selectedLayerId,
            currentFrame,
            layerKeyframes: objectKeyframes,
            selectedKeyframe,
        })
        : null;
    const currentKeyframe = stateTarget?.kind === 'keyframe' ? stateTarget.keyframe : undefined;
    const selectedKeyframeRecord = selectedKeyframe && selectedLayerId === selectedKeyframe.layerId
        ? objectKeyframes.find((kf) => kf.frame === selectedKeyframe.frame)
        : undefined;
    const sortedLayerKeyframes = useMemo(() => [...objectKeyframes].sort((a, b) => a.frame - b.frame), [objectKeyframes]);
    const selectedKeyframeIndex = selectedKeyframeRecord
        ? sortedLayerKeyframes.findIndex((kf) => kf.frame === selectedKeyframeRecord.frame)
        : -1;
    const previousKeyframe = selectedKeyframeIndex > 0 ? sortedLayerKeyframes[selectedKeyframeIndex - 1] : undefined;
    const nextKeyframe = selectedKeyframeIndex >= 0 && selectedKeyframeIndex < sortedLayerKeyframes.length - 1
        ? sortedLayerKeyframes[selectedKeyframeIndex + 1]
        : undefined;
    const keyframeStateObjects = useMemo(() => {
        if (!selectedKeyframeRecord)
            return [];
        const sceneObjects = currentScene?.objects ?? [];
        const rows = Object.entries(selectedKeyframeRecord.objectStates ?? {}).map(([id, state]) => {
            const obj = sceneObjects.find((item) => item.id === id);
            return { id, name: obj?.name || obj?.type || id, state };
        });
        const primaryId = layerForSelection?.objectId;
        if (primaryId && !rows.some((row) => row.id === primaryId)) {
            const obj = sceneObjects.find((item) => item.id === primaryId);
            rows.unshift({
                id: primaryId,
                name: obj?.name || obj?.type || primaryId,
                state: selectedKeyframeRecord.state,
            });
        }
        return rows.sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedKeyframeRecord, currentScene?.objects, layerForSelection?.objectId]);
    useEffect(() => {
        setStateObjectId(null);
    }, [selectedKeyframe?.layerId, selectedKeyframe?.frame]);
    const currentTween = selectedTween
        ? objectTweens.find((tw) => tw.startFrame === selectedTween.startFrame)
        : objectTweens.find((tw) => tw.startFrame <= currentFrame && tw.endFrame > currentFrame);
    const persistLayoutProperties = useCallback(async (obj, keyFrameId) => {
        const sceneObjectId = Number.parseInt(obj.id, 10);
        if (!Number.isFinite(sceneObjectId))
            return;
        try {
            if (signalRStatus !== 'Connected')
                await connect();
            await sceneObjectPatchStateProperties({
                sceneObjectId,
                keyFrameId,
                patch: sceneObjectTransformPatchJson(obj),
            });
        }
        catch (error) {
            console.error('[ObjectProperties] Failed to persist layout properties:', error);
        }
    }, [connect, sceneObjectPatchStateProperties, signalRStatus]);
    const schedulePersistLayout = useCallback((obj, keyFrameId) => {
        if (layoutPersistTimerRef.current)
            clearTimeout(layoutPersistTimerRef.current);
        layoutPersistTimerRef.current = setTimeout(() => {
            void persistLayoutProperties(obj, keyFrameId);
        }, 300);
    }, [persistLayoutProperties]);
    const handlePropertyChange = useCallback((prop, value) => {
        if (!selectedObject)
            return;
        // Keep aspect ratio for width/height edits if enabled
        const patch = { [prop]: value };
        if (keepAspectRatio && (prop === 'width' || prop === 'height')) {
            const startW = selectedObject.width;
            const startH = selectedObject.height;
            const ratio = startH > 0 ? startW / startH : 0;
            if (ratio > 0) {
                if (prop === 'width' && typeof value === 'number') {
                    patch.height = Math.max(1, Math.round(value / ratio));
                }
                if (prop === 'height' && typeof value === 'number') {
                    patch.width = Math.max(1, Math.round(value * ratio));
                }
            }
        }
        if (selectedObject.type === 'text' && (prop === 'width' || prop === 'height')) {
            const nextHeight = typeof patch.height === 'number' ? patch.height : selectedObject.height;
            const nextFontSize = fontSizeForTextResize(selectedObject.fontSize ?? 16, selectedObject.height, nextHeight);
            if (nextFontSize !== (selectedObject.fontSize ?? 16)) {
                patch.fontSize = nextFontSize;
            }
        }
        const isLayoutProp = prop === 'x' ||
            prop === 'y' ||
            prop === 'width' ||
            prop === 'height' ||
            prop === 'rotation' ||
            prop === 'opacity';
        const nextObject = { ...selectedObject, ...patch };
        const scenePatch = stateTarget?.kind === 'default' && isLayoutProp
            ? {
                ...patch,
                defaultPose: {
                    x: nextObject.x,
                    y: nextObject.y,
                    width: nextObject.width,
                    height: nextObject.height,
                    rotation: nextObject.rotation,
                    opacity: nextObject.opacity,
                },
            }
            : patch;
        // Update the scene object
        updateSceneObject(selectedObject.id, scenePatch);
        if (patch.fontSize != null) {
            if (textPersistTimerRef.current)
                clearTimeout(textPersistTimerRef.current);
            const textObject = { ...nextObject, fontSize: patch.fontSize };
            textPersistTimerRef.current = setTimeout(() => {
                const sceneObjectId = Number.parseInt(textObject.id, 10);
                if (!Number.isFinite(sceneObjectId))
                    return;
                void (async () => {
                    try {
                        if (signalRStatus !== 'Connected')
                            await connect();
                        await sceneObjectUpdateContent({
                            id: sceneObjectId,
                            type: 'text',
                            body: serializeTextBody(textBodyFromSceneObject(textObject)),
                        });
                    }
                    catch (error) {
                        console.error('[ObjectProperties] Failed to persist text font size:', error);
                    }
                })();
            }, 300);
        }
        // Aktif keyframe + (çoklu nesne katmanında) objectStates — Stage commitLayoutAfterTransform ile aynı mantık
        if (selectedLayerId &&
            currentKeyframe &&
            isLayoutProp) {
            const layer = layers.find((l) => l.id === selectedLayerId);
            const isPrimary = layer?.objectId === selectedObject.id;
            const baseState = currentKeyframe.objectStates?.[selectedObject.id] ??
                (isPrimary
                    ? currentKeyframe.state
                    : {
                        x: selectedObject.x,
                        y: selectedObject.y,
                        width: selectedObject.width,
                        height: selectedObject.height,
                        rotation: selectedObject.rotation,
                        opacity: selectedObject.opacity,
                    });
            const statePatch = prop === 'width' || prop === 'height'
                ? {
                    ...baseState,
                    ...(patch.width != null ? { width: patch.width } : {}),
                    ...(patch.height != null ? { height: patch.height } : {}),
                }
                : { ...baseState, [prop]: value };
            if (isPrimary) {
                const objectStates = currentKeyframe.objectStates
                    ? { ...currentKeyframe.objectStates, [selectedObject.id]: statePatch }
                    : undefined;
                updateKeyframe(selectedLayerId, currentKeyframe.frame, {
                    state: statePatch,
                    ...(objectStates ? { objectStates } : {}),
                });
            }
            else {
                updateKeyframe(selectedLayerId, currentKeyframe.frame, {
                    objectStates: { ...(currentKeyframe.objectStates ?? {}), [selectedObject.id]: statePatch },
                });
            }
        }
        // Stage drag/resize ile aynı: layout alanlarını state properties'e yaz.
        if (isLayoutProp) {
            const keyFrameId = stateTarget?.kind === 'keyframe' ? stateTarget.keyframeId : 0;
            if (stateTarget?.kind === 'keyframe' && keyFrameId == null)
                return;
            schedulePersistLayout(nextObject, keyFrameId ?? 0);
        }
        if (prop === 'visible' && typeof value === 'boolean') {
            const parsedId = Number(selectedObject.id);
            if (!Number.isFinite(parsedId))
                return;
            void (async () => {
                try {
                    if (signalRStatus !== 'Connected') {
                        await connect();
                    }
                    await sceneObjectSetVisibility({ id: parsedId, isVisible: value });
                }
                catch (error) {
                    console.error('[ObjectProperties] Failed to persist visibility:', error);
                    updateSceneObject(selectedObject.id, { visible: !value });
                }
            })();
        }
        if (prop === 'locked' && typeof value === 'boolean') {
            const parsedId = Number(selectedObject.id);
            if (!Number.isFinite(parsedId))
                return;
            void (async () => {
                try {
                    if (signalRStatus !== 'Connected') {
                        await connect();
                    }
                    await sceneObjectSetLock({ id: parsedId, isLocked: value });
                }
                catch (error) {
                    console.error('[ObjectProperties] Failed to persist lock:', error);
                    updateSceneObject(selectedObject.id, { locked: !value });
                }
            })();
        }
    }, [
        selectedObject,
        selectedLayerId,
        layers,
        updateSceneObject,
        currentKeyframe,
        updateKeyframe,
        keepAspectRatio,
        stateTarget,
        schedulePersistLayout,
        signalRStatus,
        connect,
        sceneObjectUpdateContent,
        sceneObjectSetVisibility,
        sceneObjectSetLock,
    ]);
    const handleEasingChange = async (easing) => {
        if (!currentTween || !selectedLayerId || !currentScene || !currentMovieResponse)
            return;
        const previousEasing = currentTween.easing;
        updateTween(currentTween.id, { easing });
        const tweenId = parseInt(currentTween.id, 10);
        const layerIdNum = parseInt(selectedLayerId, 10);
        if (isNaN(tweenId) || isNaN(layerIdNum))
            return;
        const layerKeyframes = keyframes[selectedLayerId] ?? [];
        const startKf = layerKeyframes.find((kf) => kf.frame === currentTween.startFrame);
        const endKf = layerKeyframes.find((kf) => kf.frame === currentTween.endFrame);
        if (!startKf || !endKf)
            return;
        const startKeyframeId = parseInt(startKf.id, 10);
        const endKeyframeId = parseInt(endKf.id, 10);
        if (isNaN(startKeyframeId) || isNaN(endKeyframeId))
            return;
        try {
            await tweenSave({
                id: tweenId,
                movieId: currentMovieResponse.id,
                sceneId: parseInt(currentScene.id, 10),
                layerId: layerIdNum,
                startKeyFrameId: startKeyframeId,
                endKeyFrameId: endKeyframeId,
                easing,
                name: '',
                description: '',
            });
        }
        catch (error) {
            console.error('Failed to save tween easing:', error);
            updateTween(currentTween.id, { easing: previousEasing });
        }
    };
    const handleCreateKeyframe = () => {
        if (!selectedObject || !selectedLayerId || !currentScene)
            return;
        const layer = layers.find((l) => l.id === selectedLayerId);
        if (!layer)
            return;
        const layerKfs = keyframes[selectedLayerId] ?? [];
        const { state, objectStates } = seedNewKeyframeState(layer, currentFrame, layerKfs, currentScene);
        const newKeyframe = {
            id: generateId('kf'),
            layerId: selectedLayerId,
            frame: currentFrame,
            state,
            ...(objectStates ? { objectStates } : {}),
        };
        addKeyframe(newKeyframe);
    };
    const copyStateToNeighbor = useCallback(async (direction) => {
        const source = selectedKeyframeRecord;
        const target = direction === 'previous' ? previousKeyframe : nextKeyframe;
        if (!source || !target || !selectedLayerId || !stateObjectId || copyingDirection)
            return;
        const layer = layers.find((item) => item.id === selectedLayerId);
        const sourceState = source.objectStates?.[stateObjectId] ??
            (layer?.objectId === stateObjectId ? source.state : undefined);
        if (!sourceState)
            return;
        const copied = { ...sourceState };
        const isPrimary = layer?.objectId === stateObjectId;
        const previousTargetState = target.state;
        const previousTargetObjectStates = target.objectStates;
        updateKeyframe(selectedLayerId, target.frame, {
            ...(isPrimary ? { state: copied } : {}),
            objectStates: { ...(target.objectStates ?? {}), [stateObjectId]: copied },
        });
        const sceneObjectId = Number.parseInt(stateObjectId, 10);
        const keyFrameId = Number.parseInt(target.id, 10);
        if (!Number.isFinite(sceneObjectId) || !Number.isFinite(keyFrameId))
            return;
        setCopyingDirection(direction);
        try {
            if (signalRStatus !== 'Connected')
                await connect();
            await sceneObjectPatchStateProperties({
                sceneObjectId,
                keyFrameId,
                patch: keyframeStatePatchJson(copied),
            });
        }
        catch (error) {
            console.error('[ObjectProperties] Failed to copy keyframe state:', error);
            updateKeyframe(selectedLayerId, target.frame, {
                state: previousTargetState,
                objectStates: previousTargetObjectStates,
            });
        }
        finally {
            setCopyingDirection(null);
        }
    }, [
        selectedKeyframeRecord,
        previousKeyframe,
        nextKeyframe,
        selectedLayerId,
        stateObjectId,
        copyingDirection,
        layers,
        updateKeyframe,
        signalRStatus,
        connect,
        sceneObjectPatchStateProperties,
    ]);
    return (_jsx(Toolpad, { title: t('rightSidebar.properties.title'), isOpen: isOpen, onToggle: () => toggleToolpad('objectProperties'), children: _jsxs("div", { className: "object-properties", children: [showSelectionSwitcher && (_jsxs("div", { className: "property-section properties-selection-switcher", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.selectedObjects', {
                                count: selectedObjectsFromScene.length,
                            }) }), _jsxs(Select.Root, { value: selectedObjectFromScene?.id ?? '', onValueChange: setFocused, size: "1", children: [_jsx(Select.Trigger, { variant: "surface", className: "style-select-trigger", "aria-label": t('rightSidebar.properties.selectedObjectAria') }), _jsx(Select.Content, { position: "popper", sideOffset: 4, children: selectedObjectsFromScene.map((obj) => (_jsx(Select.Item, { value: obj.id, children: obj.name || obj.type }, obj.id))) })] })] })), showProperties ? (_jsxs(_Fragment, { children: [(selectedObject && hasTimelineSelection) && (_jsxs("div", { className: "properties-tabs", children: [_jsx("button", { className: `properties-tab ${activeTab === 'object' ? 'active' : ''}`, onClick: () => setActiveTab('object'), children: t('rightSidebar.properties.object') }), _jsx("button", { className: `properties-tab ${activeTab === 'tween' ? 'active' : ''}`, onClick: () => setActiveTab('tween'), children: t('rightSidebar.properties.tween') })] })), activeTab === 'object' && selectedObject && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.stageTools') }), _jsxs("div", { className: "properties-stage-toggles", children: [_jsx("button", { type: "button", className: `properties-stage-toggle ${stageResizeEnabled ? 'active' : ''}`, "aria-pressed": stageResizeEnabled, title: t('rightSidebar.properties.stageResizeHint'), onClick: toggleStageResizeEnabled, children: t('rightSidebar.properties.stageResize') }), _jsx("button", { type: "button", className: `properties-stage-toggle ${stageRotateEnabled ? 'active' : ''}`, "aria-pressed": stageRotateEnabled, title: t('rightSidebar.properties.stageRotateHint'), onClick: toggleStageRotateEnabled, children: t('rightSidebar.properties.stageRotate') })] })] }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.transform') }), _jsxs("div", { className: "property-row-full", style: { marginBottom: 8 }, children: [_jsxs("label", { className: "checkbox-label", style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("input", { type: "checkbox", checked: keepAspectRatio, onChange: toggleKeepAspectRatio }), t('rightSidebar.properties.keepAspectRatio')] }), _jsx("div", { style: { fontSize: 12, opacity: 0.7, marginTop: 4 }, children: t('rightSidebar.properties.keepAspectRatioHint') })] }), _jsxs("div", { className: "property-grid", children: [_jsx(PropertyInput, { label: "X", value: selectedObject.x, onChange: (v) => handlePropertyChange('x', v) }), _jsx(PropertyInput, { label: "Y", value: selectedObject.y, onChange: (v) => handlePropertyChange('y', v) }), _jsx(PropertyInput, { label: "W", value: selectedObject.width, onChange: (v) => handlePropertyChange('width', v), min: 1 }), _jsx(PropertyInput, { label: "H", value: selectedObject.height, onChange: (v) => handlePropertyChange('height', v), min: 1 })] })] }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.appearance') }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.properties.rotation') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: -180, max: 180, value: selectedObject.rotation, onChange: (e) => handlePropertyChange('rotation', parseInt(e.target.value)), "aria-label": t('rightSidebar.properties.rotationAria') }), _jsx(PropertyInput, { className: "property-input-inline", value: selectedObject.rotation, min: -180, max: 180, onChange: (v) => handlePropertyChange('rotation', v), "aria-label": t('rightSidebar.properties.rotationDegreesAria') }), _jsx("span", { className: "unit", children: "\u00B0" })] })] }), _jsxs("div", { className: "property-row-full", children: [_jsx("label", { className: "label", children: t('rightSidebar.properties.opacity') }), _jsxs("div", { className: "property-input-with-slider", children: [_jsx("input", { type: "range", min: 0, max: 100, value: selectedObject.opacity * 100, onChange: (e) => handlePropertyChange('opacity', parseInt(e.target.value) / 100), "aria-label": t('rightSidebar.properties.opacityAria') }), _jsx(PropertyInput, { className: "property-input-inline", value: Math.round(selectedObject.opacity * 100), min: 0, max: 100, onChange: (v) => handlePropertyChange('opacity', v / 100), "aria-label": t('rightSidebar.properties.opacityPercentAria') }), _jsx("span", { className: "unit", children: "%" })] })] })] }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.visibility') }), _jsxs("div", { className: "property-checkboxes", children: [_jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: selectedObject.visible, onChange: (e) => handlePropertyChange('visible', e.target.checked) }), t('common.visible')] }), _jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: selectedObject.locked, onChange: (e) => handlePropertyChange('locked', e.target.checked) }), t('common.locked')] })] })] })] })), activeTab === 'tween' && (_jsxs(_Fragment, { children: [currentTween ? (_jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.activeTween', {
                                                start: currentTween.startFrame,
                                                end: currentTween.endFrame,
                                            }) }), _jsxs("div", { className: "property-row-full", children: [_jsx("span", { className: "label", id: "easing-options-label", children: t('rightSidebar.properties.easing') }), _jsx("div", { className: "easing-options", role: "listbox", "aria-labelledby": "easing-options-label", "aria-label": t('rightSidebar.properties.easingAria'), children: EASING_OPTIONS.map((easing) => {
                                                        const selected = currentTween.easing === easing;
                                                        return (_jsx("button", { type: "button", role: "option", "aria-selected": selected, className: `easing-option${selected ? ' selected' : ''}`, onClick: () => {
                                                                if (!selected)
                                                                    void handleEasingChange(easing);
                                                            }, children: t(EASING_LABEL_KEYS[easing]) }, easing));
                                                    }) })] }), _jsx("div", { className: "easing-preview", children: _jsx(EasingPreview, { easing: currentTween.easing }) })] })) : (_jsxs("div", { className: "no-tween-message", children: [_jsx("p", { children: t('rightSidebar.properties.noTween') }), _jsx("p", { className: "hint", children: t('rightSidebar.properties.noTweenHint') })] })), objectTweens.length > 0 && (_jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.allTweens') }), _jsx("div", { className: "tween-list", children: objectTweens.map((tw) => (_jsxs("div", { className: `tween-item ${tw === currentTween ? 'active' : ''}`, children: [_jsx("span", { children: t('rightSidebar.properties.frameRange', { start: tw.startFrame, end: tw.endFrame }) }), _jsx("span", { className: "tween-easing", children: tw.easing })] }, tw.id))) })] }))] }))] })) : (_jsx("div", { className: "no-selection-message", children: t('rightSidebar.properties.noSelection') })), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.keyframe') }), _jsxs("div", { className: "keyframe-info", children: [_jsxs("div", { className: "keyframe-frame-info", children: [_jsx("span", { className: "keyframe-frame-label", children: t('rightSidebar.properties.frame') }), _jsx("span", { className: "keyframe-frame-number", children: currentFrame })] }), currentKeyframe ? (_jsx("span", { className: "keyframe-badge", children: t('rightSidebar.properties.activeKeyframe', { frame: currentKeyframe.frame }) })) : (_jsx("span", { className: "no-keyframe", children: t('rightSidebar.properties.noKeyframe') })), _jsx("button", { className: "btn btn-small", onClick: handleCreateKeyframe, disabled: !hasKeyframeSelection || !selectedObject || !selectedLayerId, title: !hasKeyframeSelection ? t('rightSidebar.properties.selectFrameHint') : undefined, children: currentKeyframe ? t('rightSidebar.properties.updateKeyframe') : t('rightSidebar.properties.createKeyframe') }), selectedKeyframeRecord && (_jsxs("div", { className: "keyframe-state-objects", children: [_jsx("div", { className: "property-section-title", children: t('rightSidebar.properties.stateObjects') }), keyframeStateObjects.length === 0 ? (_jsx("span", { className: "no-keyframe", children: t('rightSidebar.properties.noStateObjects') })) : (keyframeStateObjects.map((row) => {
                                            const selected = stateObjectId === row.id;
                                            return (_jsxs("div", { className: `keyframe-state-object${selected ? ' selected' : ''}`, children: [_jsx("button", { type: "button", className: "keyframe-state-object-name", "aria-pressed": selected, onClick: () => setStateObjectId(selected ? null : row.id), children: row.name }), selected && (_jsxs("div", { className: "keyframe-state-actions", children: [_jsx("button", { type: "button", className: "btn btn-tiny", disabled: !previousKeyframe || copyingDirection != null, title: previousKeyframe
                                                                    ? t('rightSidebar.properties.copyStateToPrevious', {
                                                                        frame: previousKeyframe.frame,
                                                                    })
                                                                    : t('rightSidebar.properties.noPreviousKeyframe'), onClick: () => void copyStateToNeighbor('previous'), children: previousKeyframe
                                                                    ? t('rightSidebar.properties.copyStateToPrevious', {
                                                                        frame: previousKeyframe.frame,
                                                                    })
                                                                    : t('rightSidebar.properties.noPreviousKeyframe') }), _jsx("button", { type: "button", className: "btn btn-tiny", disabled: !nextKeyframe || copyingDirection != null, title: nextKeyframe
                                                                    ? t('rightSidebar.properties.copyStateToNext', {
                                                                        frame: nextKeyframe.frame,
                                                                    })
                                                                    : t('rightSidebar.properties.noNextKeyframe'), onClick: () => void copyStateToNeighbor('next'), children: nextKeyframe
                                                                    ? t('rightSidebar.properties.copyStateToNext', {
                                                                        frame: nextKeyframe.frame,
                                                                    })
                                                                    : t('rightSidebar.properties.noNextKeyframe') })] }))] }, row.id));
                                        }))] }))] })] })] }) }));
}
function EasingPreview({ easing }) {
    // Simple SVG preview of easing curve
    const getPath = (type) => {
        switch (type) {
            case 'linear':
                return 'M 0 40 L 60 0';
            case 'easeIn':
                return 'M 0 40 Q 30 40 60 0';
            case 'easeOut':
                return 'M 0 40 Q 30 0 60 0';
            case 'easeInOut':
                return 'M 0 40 C 20 40, 40 0, 60 0';
            case 'bounce':
                return 'M 0 40 Q 20 0 30 20 Q 40 40 45 30 Q 50 20 60 0';
            case 'elastic':
                return 'M 0 40 Q 10 45 20 35 Q 30 -5 40 10 Q 50 0 60 0';
            case 'back':
                return 'M 0 40 Q 15 50 30 20 Q 45 -10 60 0';
            default:
                return 'M 0 40 L 60 0';
        }
    };
    return (_jsx("svg", { width: "60", height: "40", className: "easing-svg", children: _jsx("path", { d: getPath(easing), fill: "none", stroke: "#4cff00", strokeWidth: "2" }) }));
}
function AlignToolpad() {
    const { t } = useTranslation();
    const { toolpads, toggleToolpad } = useUIStore();
    const { currentScene, updateSceneObject, currentMovie } = useMovieStore();
    const { selectedIds } = useSelectionStore();
    const isOpen = toolpads.alignPad?.isOpen ?? false;
    const selectedObject = currentScene?.objects.find((o) => selectedIds.includes(o.id));
    const stageWidth = currentMovie?.width ?? 800;
    const stageHeight = currentMovie?.height ?? 600;
    const alignLeft = () => {
        if (selectedObject) {
            updateSceneObject(selectedObject.id, { x: 0 });
        }
    };
    const alignCenter = () => {
        if (selectedObject) {
            updateSceneObject(selectedObject.id, {
                x: Math.round((stageWidth - selectedObject.width) / 2),
            });
        }
    };
    const alignRight = () => {
        if (selectedObject) {
            updateSceneObject(selectedObject.id, { x: stageWidth - selectedObject.width });
        }
    };
    const alignTop = () => {
        if (selectedObject) {
            updateSceneObject(selectedObject.id, { y: 0 });
        }
    };
    const alignMiddle = () => {
        if (selectedObject) {
            updateSceneObject(selectedObject.id, {
                y: Math.round((stageHeight - selectedObject.height) / 2),
            });
        }
    };
    const alignBottom = () => {
        if (selectedObject) {
            updateSceneObject(selectedObject.id, { y: stageHeight - selectedObject.height });
        }
    };
    return (_jsx(Toolpad, { title: t('rightSidebar.align.title'), isOpen: isOpen, onToggle: () => toggleToolpad('alignPad'), children: _jsxs("div", { className: "align-pad", children: [_jsxs("div", { className: "align-group", children: [_jsx("div", { className: "align-label", children: t('rightSidebar.align.horizontal') }), _jsxs("div", { className: "align-buttons", children: [_jsx("button", { type: "button", className: "align-btn", onClick: alignLeft, disabled: !selectedObject, title: t('rightSidebar.align.left'), "aria-label": t('rightSidebar.align.left'), children: _jsx(AlignLeftIcon, { width: 16, height: 16 }) }), _jsx("button", { type: "button", className: "align-btn", onClick: alignCenter, disabled: !selectedObject, title: t('rightSidebar.align.center'), "aria-label": t('rightSidebar.align.center'), children: _jsx(AlignCenterHorizontallyIcon, { width: 16, height: 16 }) }), _jsx("button", { type: "button", className: "align-btn", onClick: alignRight, disabled: !selectedObject, title: t('rightSidebar.align.right'), "aria-label": t('rightSidebar.align.right'), children: _jsx(AlignRightIcon, { width: 16, height: 16 }) })] })] }), _jsxs("div", { className: "align-group", children: [_jsx("div", { className: "align-label", children: t('rightSidebar.align.vertical') }), _jsxs("div", { className: "align-buttons", children: [_jsx("button", { type: "button", className: "align-btn", onClick: alignTop, disabled: !selectedObject, title: t('rightSidebar.align.top'), "aria-label": t('rightSidebar.align.top'), children: _jsx(AlignTopIcon, { width: 16, height: 16 }) }), _jsx("button", { type: "button", className: "align-btn", onClick: alignMiddle, disabled: !selectedObject, title: t('rightSidebar.align.middle'), "aria-label": t('rightSidebar.align.middle'), children: _jsx(AlignCenterVerticallyIcon, { width: 16, height: 16 }) }), _jsx("button", { type: "button", className: "align-btn", onClick: alignBottom, disabled: !selectedObject, title: t('rightSidebar.align.bottom'), "aria-label": t('rightSidebar.align.bottom'), children: _jsx(AlignBottomIcon, { width: 16, height: 16 }) })] })] })] }) }));
}
